import { useState, useEffect, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { buildSidebarBlocs } from "../lib/sidebar";
import { useSetAsset } from "../hooks/useSetAsset";
import { handleSetAssetError } from "../lib/images";
import { ERA_IMAGE, ERA_SHORT, ERA_LOGO_SET, OTHER_ERA_BLOCS } from "../config/eras";
import { ProgressBar } from "./ProgressBar";

// Refonte du 2026-09-26 (maquette validée) : un RAIL d'ères à gauche (une
// image par bloc + « Tout » en tête), et à droite la liste des sets de l'ère
// choisie, chacun avec son LOGO et une barre de progression. Plus rien à
// déplier : on clique une ère, on voit ses sets.
// 2e passe (retour PC, même jour) : rail plus large et logos en couleur
// (l'ancien rail de 64 px en niveaux de gris était illisible), Platine /
// Appel des Légendes / Pocket prennent le logo d'un de leurs sets, et les
// blocs « produits » (promos, POP, jumbos, kits, McDo, WC, Pocket) sont
// regroupés sous l'ère « Autre » en sections nommées.
const ERA_STORAGE_KEY = "bulbizarbi_sidebar_era";

const readEra = () => { try { return localStorage.getItem(ERA_STORAGE_KEY) || null; } catch (e) { return null; } };
const writeEra = (k) => { try { localStorage.setItem(ERA_STORAGE_KEY, k); } catch (e) {} };

// Regroupe les blocs de la sidebar en « ères » du rail : chaque grande époque
// reste un bouton, tout le reste (OTHER_ERA_BLOCS + l'entrée « Autre » des
// séries orphelines) fusionne dans une seule ère « Autre » à sections.
function buildEras(blocs) {
  const eras = [];
  const otherSubs = [];
  blocs.forEach(b => {
    if (OTHER_ERA_BLOCS.has(b.key)) {
      otherSubs.push({ key: b.key, label: b.label, sets: b.sets });
    } else if (b.key === "other") {
      (b.subBlocs || [{ key: "other", label: b.label, sets: b.sets }]).forEach(s => otherSubs.push(s));
    } else {
      eras.push(b);
    }
  });
  if (otherSubs.length) {
    eras.push({ key: "other", label: "Autre", sets: otherSubs.flatMap(s => s.sets), subBlocs: otherSubs });
  }
  return eras;
}

const SidebarSetRow = ({ set, isActive, pct, counts, onSelectSet }) => {
  const logoSrc = useSetAsset(set.id, "logo", set.logo);
  // Symbole de set (le petit pictogramme imprimé en bas à gauche des cartes)
  // à côté du nom : c'est par lui que les collectionneurs reconnaissent une
  // série (demande utilisateur du 2026-10-03).
  const symbolSrc = useSetAsset(set.id, "symbol", set.symbol);
  const total = counts?.total ?? set.cardCount?.total ?? set.cardCount?.official ?? 0;
  const owned = counts?.owned ?? 0;
  return (
    <button className={`sb-set ${isActive ? "on" : ""}`} onClick={() => onSelectSet(set.id)} title={set.name}>
      <span className="sb-logo">
        {logoSrc
          ? <img src={logoSrc} alt="" onError={handleSetAssetError} />
          : <span className="sb-logo-fallback">{set.name.slice(0, 3)}</span>}
      </span>
      <span className="sb-txt">
        <span className="sb-name">
          {/* Le chip entier disparaît si le symbole n'existe pas chez TCGdex
              (404 après la retentative .png de handleSetAssetError) — sinon
              un carré vide restait devant le nom. */}
          {symbolSrc && (
            <span className="sb-symbol">
              <img src={symbolSrc} alt="" onError={(e) => {
                handleSetAssetError(e);
                if (e.currentTarget.style.display === "none") e.currentTarget.parentElement.style.display = "none";
              }} />
            </span>
          )}
          {set.name}
        </span>
        <ProgressBar pct={pct} owned={owned} total={total} />
      </span>
    </button>
  );
};

// Image d'une ère : image dédiée (ERA_IMAGE), sinon logo d'un set de
// référence (ERA_LOGO_SET, chargé comme n'importe quel logo de set), sinon rien.
const useEraImage = (era, setById) => {
  const refSet = ERA_LOGO_SET[era.key] ? setById[ERA_LOGO_SET[era.key]] : null;
  const logoSrc = useSetAsset(refSet?.id || null, "logo", refSet?.logo);
  return ERA_IMAGE[era.key] || logoSrc || null;
};

const EraButton = ({ era, active, onClick, setById }) => {
  const src = useEraImage(era, setById);
  return (
    <button className={`sb-era ${src ? "img" : "txt"} ${active ? "on" : ""}`} onClick={onClick} title={era.label}>
      {src
        ? <img src={src} alt={era.label} draggable="false" onError={handleSetAssetError} />
        : <span>{ERA_SHORT[era.key] || era.label}</span>}
    </button>
  );
};

const EraHead = ({ era, started }) => {
  return (
    <div className="sb-head">
      <span className="sb-head-title">{era.label}</span>
      <span className="sb-head-sub">
        {started} {started > 1 ? "sets commencés" : "set commencé"} sur {era.sets.length}
      </span>
    </div>
  );
};

export const Sidebar = ({
  seriesData, activeSetId, onSelectSet, completionMap, completionCounts = {}, sidebarPrefs,
  mobileOpen = false, onClose, navActions = [],
}) => {
  const blocs = useMemo(() => buildSidebarBlocs(seriesData, sidebarPrefs), [seriesData, sidebarPrefs]);
  const eras = useMemo(() => buildEras(blocs), [blocs]);
  const setById = useMemo(() => {
    const m = {};
    (seriesData || []).forEach(s => (s.sets || []).forEach(set => { m[set.id] = set; }));
    return m;
  }, [seriesData]);
  const [era, setEra] = useState(() => readEra() || "all");
  // Menu (Explorer, Pokédex, Réglages…) replié par défaut dans le tiroir
  // mobile : les collections doivent rester visibles sans scroll (retour
  // utilisateur 2026-09-27, cf. .sidebar-mobile-nav-toggle dans App.css).
  const [navOpen, setNavOpen] = useState(false);

  // Le set ouvert sélectionne son ère (sinon on ne le verrait pas surligné).
  useEffect(() => {
    if (!activeSetId) return;
    const found = eras.find(e => e.sets.some(s => s.id === activeSetId));
    if (found && era !== "all" && era !== found.key) setEra(found.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSetId, eras]);

  // Une ère mémorisée qui n'existe plus (masquée dans les Réglages) → « Tout ».
  useEffect(() => {
    if (era !== "all" && eras.length && !eras.some(e => e.key === era)) setEra("all");
  }, [eras, era]);

  const pick = (k) => { setEra(k); writeEra(k); };

  const startedIn = (sets) => sets.filter(s => completionMap[s.id] > 0).length;
  const renderSet = (set) => (
    <SidebarSetRow key={set.id} set={set} isActive={activeSetId === set.id}
      pct={completionMap[set.id]} counts={completionCounts[set.id]} onSelectSet={onSelectSet} />
  );

  const current = era === "all" ? null : eras.find(e => e.key === era);

  return (
    <>
      {mobileOpen && <div className="sidebar-backdrop" onClick={onClose} />}
      <nav className={`sidebar ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="sb-rail">
          <button className={`sb-era all ${era === "all" ? "on" : ""}`} onClick={() => pick("all")} title="Toutes les collections">
            <span>Tout</span>
          </button>
          {eras.map(e => (
            <EraButton key={e.key} era={e} active={era === e.key} onClick={() => pick(e.key)} setById={setById} />
          ))}
        </div>

        <div className="sb-list">
          {navActions.length > 0 && (
            <div className="sidebar-mobile-nav">
              <button className="sidebar-mobile-nav-toggle" onClick={() => setNavOpen(v => !v)}>
                <span>Menu</span>
                <ChevronDown className={navOpen ? "rotate-180" : ""} style={{ width: 16, height: 16 }} />
              </button>
              {navOpen && (
                <div className="sidebar-mobile-nav-list">
                  {navActions.map(({ key, icon: Icon, label, onClick }) => (
                    <button key={key} className="sidebar-mobile-nav-btn" onClick={onClick}>
                      <Icon style={{ width: 16, height: 16, flexShrink: 0 }} />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {current ? (
            <>
              <EraHead era={current} started={startedIn(current.sets)} />
              {!current.subBlocs && current.sets.map(renderSet)}
              {current.subBlocs && current.subBlocs.map(sub => (
                <div key={sub.key}>
                  <div className="sb-section"><span>{sub.label}</span><em>{startedIn(sub.sets)} / {sub.sets.length}</em></div>
                  {sub.sets.map(renderSet)}
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="sb-head">
                <span className="sb-head-title">Toutes les collections</span>
                <span className="sb-head-sub">{eras.reduce((n, e) => n + e.sets.length, 0)} sets</span>
              </div>
              {eras.map(e => (
                <div key={e.key}>
                  <button className="sb-section sb-section-btn" onClick={() => pick(e.key)} title={`Voir seulement ${e.label}`}>
                    <span>{e.label}</span><em>{startedIn(e.sets)} / {e.sets.length}</em>
                  </button>
                  {e.sets.map(renderSet)}
                </div>
              ))}
            </>
          )}
        </div>
      </nav>
    </>
  );
};
